import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

export interface CocoMessage {
  id?: string;
  role: 'user' | 'assistant';
  content: string;
}

interface CocoContextValue {
  messages: CocoMessage[];
  streamingContent: string;
  isStreaming: boolean;
  sendMessage: (prompt: string) => Promise<void>;
}

const CocoContext = createContext<CocoContextValue | undefined>(undefined);

export const CocoProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [conversationId, setConversationId] = useState<string | null>(null);
  const [messages, setMessages] = useState<CocoMessage[]>([]);
  const [streamingContent, setStreamingContent] = useState('');
  const [isStreaming, setIsStreaming] = useState(false);
  const abortControllerRef = useRef<AbortController | null>(null);

  const getAuthHeaders = (): HeadersInit => {
    const token = localStorage.getItem('support-app-token');
    return {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
    };
  };

  const createConversation = useCallback(async (): Promise<string | null> => {
    try {
      const response = await fetch('/api/coco/conversations', {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({ title: 'Support Chat' }),
      });
      if (response.ok) {
        const data = await response.json();
        return data.id;
      }
    } catch (error) {
      console.error('Failed to create conversation:', error);
    }
    return null;
  }, []);

  const sendMessage = useCallback(
    async (prompt: string) => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }

      let convId = conversationId;
      if (!convId) {
        convId = await createConversation();
        if (!convId) return;
        setConversationId(convId);
      }

      setMessages((prev) => [...prev, { role: 'user', content: prompt }]);
      setIsStreaming(true);
      setStreamingContent('');

      const controller = new AbortController();
      abortControllerRef.current = controller;

      try {
        const params = new URLSearchParams({
          conversation_id: convId,
          prompt,
        });
        const token = localStorage.getItem('support-app-token');
        const response = await fetch(`/api/coco/chat?${params}`, {
          headers: { Authorization: `Bearer ${token}` },
          signal: controller.signal,
        });

        if (!response.ok || !response.body) {
          throw new Error('Stream request failed');
        }

        const reader = response.body.getReader();
        const decoder = new TextDecoder();
        let accumulated = '';

        while (true) {
          const { done, value } = await reader.read();
          if (done) break;

          const text = decoder.decode(value, { stream: true });
          for (const line of text.split('\n')) {
            if (line.startsWith('event: ')) continue;
            if (line.startsWith('data: ')) {
              const data = line.slice(6).trim();
              if (data === '[DONE]' || data === '{}') continue;
              try {
                accumulated += atob(data);
                setStreamingContent(accumulated);
              } catch {
                try {
                  const parsed = JSON.parse(data);
                  if (parsed.content) {
                    accumulated += parsed.content;
                    setStreamingContent(accumulated);
                  }
                } catch {
                  if (data) {
                    accumulated += data;
                    setStreamingContent(accumulated);
                  }
                }
              }
            }
          }
        }

        if (accumulated) {
          setMessages((prev) => [...prev, { role: 'assistant', content: accumulated }]);
        }
      } catch (error: any) {
        if (error.name !== 'AbortError') {
          console.error('Chat stream error:', error);
        }
      } finally {
        setIsStreaming(false);
        setStreamingContent('');
        abortControllerRef.current = null;
      }
    },
    [conversationId, createConversation],
  );

  // New session on mount — reset everything
  useEffect(() => {
    setConversationId(null);
    setMessages([]);
  }, []);

  const value = useMemo(
    () => ({ messages, streamingContent, isStreaming, sendMessage }),
    [messages, streamingContent, isStreaming, sendMessage],
  );

  return <CocoContext.Provider value={value}>{children}</CocoContext.Provider>;
};

export const useCoco = (): CocoContextValue => {
  const context = useContext(CocoContext);
  if (!context) {
    throw new Error('useCoco must be used within a CocoProvider');
  }
  return context;
};
